import React from 'react';
import { cn } from '../../utils/cn';

const ContentSection = ({
  children,
  title,
  subtitle,
  description,
  actions,
  controls,
  className = '',
  headerClassName = '',
  contentClassName = '',
  variant = 'default', // 'default', 'card', 'bordered', 'minimal'
  spacing = 'default', // 'none', 'sm', 'default', 'lg', 'xl'
  headerSpacing = 'default', // 'none', 'sm', 'default', 'lg'
  layout = 'stack', // 'stack', 'split', 'inline'
  background = 'transparent', // 'transparent', 'white', 'gray'
  padding = 'none', // 'none', 'sm', 'default', 'lg'
  border = false,
  shadow = false,
  fullWidth = true,
  ...props
}) => {
  const sectionClasses = cn(
    'content-section',
    {
      // Spacing variants
      'mb-4': spacing === 'sm',
      'mb-6': spacing === 'default',
      'mb-8': spacing === 'lg',
      'mb-12': spacing === 'xl',
    },
    {
      // Variant styles
      'bg-white rounded-tile shadow-tile border border-gray-300': variant === 'card',
      'border border-gray-200 rounded-md': variant === 'bordered',
      'bg-white': variant === 'default' && background === 'white',
      'bg-gray-50': background === 'gray',
    },
    {
      // Padding
      'p-4': padding === 'sm',
      'p-6': padding === 'default',
      'p-8': padding === 'lg',
    },
    {
      // Shadow and border
      'shadow-sm': shadow && variant !== 'card',
      'border border-gray-200': border && variant !== 'card' && variant !== 'bordered',
    },
    {
      // Width
      'w-full': fullWidth,
    },
    className
  );

  const headerClasses = cn(
    'content-section-header',
    {
      // Header spacing
      'mb-2': headerSpacing === 'sm',
      'mb-4': headerSpacing === 'default',
      'mb-6': headerSpacing === 'lg',
    },
    {
      // Layout variants
      'flex flex-col': layout === 'stack',
      'flex justify-between items-start gap-4': layout === 'split',
      'flex items-center gap-4': layout === 'inline',
    },
    {
      // Responsive adjustments for split layout
      'flex-col gap-2 sm:flex-row sm:gap-4': layout === 'split',
    },
    headerClassName
  );

  const titleSectionClasses = cn(
    'content-section-title-section',
    {
      'flex-grow': layout === 'split',
    }
  );

  const actionSectionClasses = cn(
    'content-section-actions',
    {
      'flex items-center gap-2': layout === 'split' || layout === 'inline',
      'mt-3': layout === 'stack' && (actions || controls),
      'flex-shrink-0': layout === 'split',
    }
  );

  const contentClasses = cn(
    'content-section-content',
    contentClassName
  );

  const hasHeader = title || subtitle || description || actions || controls;

  return (
    <section className={sectionClasses} {...props}>
      {hasHeader && (
        <div className={headerClasses}>
          {/* Title Section */}
          {(title || subtitle || description) && (
            <div className={titleSectionClasses}>
              {title && (
                <h2 className={cn(
                  'content-section-title font-semibold text-gray-800',
                  {
                    'text-lg': variant === 'minimal',
                    'text-xl': variant === 'default' || variant === 'card',
                    'text-2xl': variant === 'bordered',
                  }
                )}>
                  {title}
                </h2>
              )}
              {subtitle && (
                <h3 className={cn(
                  'content-section-subtitle text-gray-600 font-medium',
                  {
                    'text-sm mt-1': title,
                    'text-base': !title,
                  }
                )}>
                  {subtitle}
                </h3>
              )}
              {description && (
                <p className={cn(
                  'content-section-description text-gray-600',
                  {
                    'text-sm mt-1': title || subtitle,
                    'text-base': !title && !subtitle,
                  }
                )}>
                  {description}
                </p>
              )}
            </div>
          )}

          {/* Actions and Controls */}
          {(actions || controls) && (
            <div className={actionSectionClasses}>
              {controls && (
                <div className="content-section-controls flex items-center gap-3">
                  {controls}
                </div>
              )}
              {actions && (
                <div className="content-section-actions-list flex items-center gap-2">
                  {actions}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className={contentClasses}>
        {children}
      </div>
    </section>
  );
};

// Content Grid - for organizing multiple content blocks
export const ContentGrid = ({
  children,
  columns = 'auto', // 'auto', 1, 2, 3, 4, 'auto-fit-200', 'auto-fit-250', 'auto-fit-300'
  gap = 'default', // 'sm', 'default', 'lg'
  responsive = true,
  className = '',
  ...props
}) => {
  const gridClasses = cn(
    'content-grid grid',
    {
      // Column variants
      'grid-cols-1': columns === 1,
      'grid-cols-1 md:grid-cols-2': columns === 2 && responsive,
      'grid-cols-2': columns === 2 && !responsive,
      'grid-cols-1 md:grid-cols-2 lg:grid-cols-3': columns === 3 && responsive,
      'grid-cols-3': columns === 3 && !responsive,
      'grid-cols-1 md:grid-cols-2 lg:grid-cols-4': columns === 4 && responsive,
      'grid-cols-4': columns === 4 && !responsive,
      'grid-cols-auto-fit-200': columns === 'auto-fit-200',
      'grid-cols-auto-fit-250': columns === 'auto-fit-250',
      'grid-cols-auto-fit-300': columns === 'auto-fit-300',
    },
    {
      // Gap variants
      'gap-4': gap === 'sm',
      'gap-6': gap === 'default',
      'gap-8': gap === 'lg',
    },
    'items-stretch',
    className
  );

  return (
    <div className={gridClasses} {...props}>
      {children}
    </div>
  );
};

// Content Row - for side-by-side layouts
export const ContentRow = ({
  children,
  gap = 'default',
  align = 'stretch', // 'start', 'center', 'end', 'stretch'
  justify = 'start', // 'start', 'center', 'end', 'between', 'around', 'evenly'
  wrap = true,
  responsive = true,
  className = '',
  ...props
}) => {
  const rowClasses = cn(
    'content-row flex',
    {
      // Flex direction - responsive by default
      'flex-col lg:flex-row': responsive && wrap,
      'flex-row': !responsive || !wrap,
      'flex-wrap': wrap && !responsive,
    },
    {
      // Gap variants
      'gap-4': gap === 'sm',
      'gap-6': gap === 'default',
      'gap-8': gap === 'lg',
    },
    {
      // Alignment
      'items-start': align === 'start',
      'items-center': align === 'center',
      'items-end': align === 'end',
      'items-stretch': align === 'stretch',
    },
    {
      // Justification
      'justify-start': justify === 'start',
      'justify-center': justify === 'center',
      'justify-end': justify === 'end',
      'justify-between': justify === 'between',
      'justify-around': justify === 'around',
      'justify-evenly': justify === 'evenly',
    },
    className
  );

  return (
    <div className={rowClasses} {...props}>
      {children}
    </div>
  );
};

// Content Column - for flex column layouts
export const ContentColumn = ({
  children,
  flex = '1', // '1', '2', 'auto', 'none', 'grow', 'shrink'
  minWidth = 'none', // 'none', 'sm', 'md', 'lg'
  className = '',
  ...props
}) => {
  const columnClasses = cn(
    'content-column',
    {
      // Flex variants
      'flex-1': flex === '1',
      'flex-2': flex === '2',
      'flex-auto': flex === 'auto',
      'flex-none': flex === 'none',
      'flex-grow': flex === 'grow',
      'flex-shrink': flex === 'shrink',
    },
    {
      // Min width variants
      'min-w-0': minWidth === 'none',
      'min-w-48': minWidth === 'sm',
      'min-w-64': minWidth === 'md',
      'min-w-80': minWidth === 'lg',
    },
    className
  );

  return (
    <div className={columnClasses} {...props}>
      {children}
    </div>
  );
};

// Content Divider
export const ContentDivider = ({
  variant = 'horizontal', // 'horizontal', 'vertical'
  spacing = 'default', // 'sm', 'default', 'lg'
  className = '',
  ...props
}) => {
  const dividerClasses = cn(
    'content-divider border-gray-200',
    {
      // Horizontal divider
      'border-t w-full': variant === 'horizontal',
      // Vertical divider
      'border-l h-full': variant === 'vertical',
    },
    {
      // Spacing for horizontal
      'my-4': variant === 'horizontal' && spacing === 'sm',
      'my-6': variant === 'horizontal' && spacing === 'default',
      'my-8': variant === 'horizontal' && spacing === 'lg',
      // Spacing for vertical
      'mx-4': variant === 'vertical' && spacing === 'sm',
      'mx-6': variant === 'vertical' && spacing === 'default',
      'mx-8': variant === 'vertical' && spacing === 'lg',
    },
    className
  );

  return <div className={dividerClasses} {...props} />;
};

// Content Action Button
export const ContentAction = ({
  children,
  variant = 'primary',
  size = 'sm',
  icon,
  disabled = false,
  loading = false,
  className = '',
  ...props
}) => {
  const buttonClasses = cn(
    'content-action inline-flex items-center justify-center font-medium transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
    {
      // Size variants
      'px-2 py-1 text-xs': size === 'xs',
      'px-3 py-1.5 text-sm': size === 'sm',
      'px-4 py-2 text-base': size === 'md',
    },
    {
      // Variant styles
      'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500': variant === 'primary',
      'bg-gray-500 text-white hover:bg-gray-600 focus:ring-gray-500': variant === 'secondary',
      'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-primary-500': variant === 'outline',
      'text-gray-700 hover:bg-gray-100 focus:ring-gray-500': variant === 'ghost',
    },
    'rounded-md',
    className
  );

  return (
    <button 
      disabled={disabled || loading} 
      className={buttonClasses} 
      {...props}
    >
      {loading && (
        <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
        </svg>
      )}
      {icon && !loading && (
        <span className={cn('inline-flex items-center', children && 'mr-2')}>
          {icon}
        </span>
      )}
      {children}
    </button>
  );
};

// Constants
export const CONTENT_SECTION_VARIANTS = {
  DEFAULT: 'default',
  CARD: 'card',
  BORDERED: 'bordered',
  MINIMAL: 'minimal',
};

export const CONTENT_SECTION_LAYOUTS = {
  STACK: 'stack',
  SPLIT: 'split',
  INLINE: 'inline',
};

export const CONTENT_SECTION_SPACING = {
  NONE: 'none',
  SMALL: 'sm',
  DEFAULT: 'default',
  LARGE: 'lg',
  EXTRA_LARGE: 'xl',
};

export default ContentSection;